import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import VendorSidebar from '../VendorSidebar';
import VendorHeader from '../VendorHeader';
import VendorLocationAlertBanner from '../../vendor/VendorLocationAlertBanner';
import { useSidebar } from '../../../contexts/SidebarContext';
import { useAuth } from '../../../contexts/AuthContext';
import useVendorPlanAccess from '../../../hooks/useVendorPlanAccess';
import { isSubscriptionExemptPath } from '../../../utils/vendorPlanAccess';
import { useSubscription } from '../../../contexts/SubscriptionContext';
import { Loader2 } from 'lucide-react';
import { DashboardThemeProvider } from '../../../contexts/DashboardThemeContext';
import ChatWidget from '../ChatWidget';

const VendorLayout = () => {
  const location = useLocation();
  const { isVendorSidebarCollapsed, isVendorMobileSidebarOpen } = useSidebar();
  const { user, isLoading: authLoading } = useAuth();
  const { isLoading: planAccessLoading, canAccessPath, hasActiveSubscription } = useVendorPlanAccess();
  const { subscriptionExpired, isChecking: subscriptionChecking } = useSubscription();

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-[#0b0f14] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-gray-900 dark:text-gray-100 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (user?.user_type === 'vendor' && (planAccessLoading || subscriptionChecking)) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-[#0b0f14] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-gray-900 dark:text-gray-100 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Checking plan access...</p>
        </div>
      </div>
    );
  }

  if (
    user?.user_type === 'vendor' &&
    (subscriptionExpired || hasActiveSubscription === false) &&
    !isSubscriptionExemptPath(location.pathname)
  ) {
    return <Navigate to="/vendor/subscription" replace />;
  }

  if (user?.user_type === 'vendor' && !canAccessPath(location.pathname)) {
    return <Navigate to="/vendor/subscription" replace />;
  }
  
  return (
    <DashboardThemeProvider variant="vendor">
      <div className="dashboard-app min-h-screen bg-gray-50 dark:bg-[#0b0f14] transition-colors">
        <VendorHeader />
        <div className="flex pt-16">
          <VendorSidebar />
          <main
            className={`dashboard-main flex-1 min-w-0 transition-all duration-300 ${
              isVendorSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
            } ${isVendorMobileSidebarOpen ? 'lg:ml-0' : ''}`}
          >
            <div className="vendor-page px-4 sm:px-6 lg:px-8 py-5 sm:py-6 min-w-0 max-w-full">
              <VendorLocationAlertBanner />
              <Outlet />
            </div>
          </main>
        </div>
        <ChatWidget />
      </div>
    </DashboardThemeProvider>
  );
};

export default VendorLayout;
